import { subCompanyTaskApi } from 'app/services/api';

import { TaskHistoryAudience, TaskStatus } from 'constants/tasks';
import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { showMessageAction } from 'store/global/actions';

const CANCEL_REASON_NOT_SAVED_MESSAGE = 'Task canceled, but the reason could not be saved.';

export const SUB_COMPANY_TASKS_ACTION_TYPE = createNamedActions('subCompanyTasks', {
  GET_TASKS: 'GET_TASKS',
  GET_TASKS_REQUEST: 'GET_TASKS_REQUEST',
  GET_TASKS_FAILURE: 'GET_TASKS_FAILURE',
  CREATE_TASK: 'CREATE_TASK',
  SUBMIT_TASK: 'SUBMIT_TASK',
  APPROVE_TASK: 'APPROVE_TASK',
  RESET_STATE: 'RESET_STATE',
});

// pendingAction / rejectedAction feed the tasksStatus branch so the Company
// Center action-items card can render its own loading + retry; catchAsyncActionError
// strips these keys from the params it forwards to the request.
export const getSubCompanyTasksAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await subCompanyTaskApi.getSubCompanyTasks(params);

  return dispatch({
    type: SUB_COMPANY_TASKS_ACTION_TYPE.GET_TASKS,
    payload: data || [],
  });
});

export const getSubCompanyTasksWithStatusAction = params =>
  getSubCompanyTasksAction({
    ...params,
    pendingAction: () => ({ type: SUB_COMPANY_TASKS_ACTION_TYPE.GET_TASKS_REQUEST }),
    rejectedAction: () => ({ type: SUB_COMPANY_TASKS_ACTION_TYPE.GET_TASKS_FAILURE }),
  });

export const createSubCompanyTaskAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await subCompanyTaskApi.createSubCompanyTask(params);

  return dispatch({
    type: SUB_COMPANY_TASKS_ACTION_TYPE.CREATE_TASK,
    payload: data,
  });
});

export const submitSubCompanyTaskAction = catchAsyncActionError(
  ({ taskId, file, submittedText }) =>
    async dispatch => {
      const { data } = await subCompanyTaskApi.submitSubCompanyTask({
        taskId,
        file,
        submittedText,
      });

      return dispatch({
        type: SUB_COMPANY_TASKS_ACTION_TYPE.SUBMIT_TASK,
        payload: data,
      });
    },
);

export const approveSubCompanyTaskAction = catchAsyncActionError(({ taskId }) => async dispatch => {
  const { data } = await subCompanyTaskApi.approveSubCompanyTask({ taskId });

  return dispatch({
    type: SUB_COMPANY_TASKS_ACTION_TYPE.APPROVE_TASK,
    payload: data,
  });
});

// The status PUT is the source of truth: once it lands the task is canceled, so a failing
// reason POST only warns instead of rejecting (which would leave the drawer open on a canceled task).
export const cancelSubCompanyTaskAction = catchAsyncActionError(
  ({ taskId, reason }) =>
    async dispatch => {
      await subCompanyTaskApi.updateSubCompanyTaskStatus({
        taskId,
        status: TaskStatus.CANCELED,
      });

      if (!reason) {
        return { isReasonSaved: true };
      }

      try {
        await subCompanyTaskApi.postSubCompanyTaskMessage({
          taskId,
          message: reason,
          audience: TaskHistoryAudience.INTERNAL_ONLY,
        });
      } catch {
        dispatch(
          showMessageAction({ message: CANCEL_REASON_NOT_SAVED_MESSAGE, variant: 'warning' }),
        );

        return { isReasonSaved: false };
      }

      return { isReasonSaved: true };
    },
);

// Persists the reassignment together with the Return transition; the reason travels as userNote
// so the backend writes it onto the Return history entry.
export const returnSubCompanyTaskAction = catchAsyncActionError(
  ({ taskId, reason, assignedUserId }) =>
    async () => {
      await subCompanyTaskApi.updateSubCompanyTaskStatus({
        taskId,
        status: TaskStatus.RETURNED,
        userNote: reason,
        assignedUserId,
      });
    },
);

export const resetSubCompanyTasksAction = () => ({
  type: SUB_COMPANY_TASKS_ACTION_TYPE.RESET_STATE,
});
